// tslint:disable:no-console
import * as React from "react";
import * as S from "@styled";
import * as _ from "lodash";
import { diff } from "bpmn-js-differ";
import modelingModule from "bpmn-js/lib/features/modeling";
import BpmnNavigatedViewer from "bpmn-js/lib/NavigatedViewer";

import { DiffEdit } from "deep-diff";

import customRenderer from "../processEditor/model/CustomRenderer";
import { IElementDictionary, IProcessData } from "../processEditor/model/IProcessData";

import { StaticFrendsElementStore } from "../processEditor/model/FrendsElementStore/StaticFrendsElementStore";
import { DiffElementModal } from "./DiffElementModal";
import { ThemeType } from "shared/styles/theme";
import { fetchTheme } from "shared/styles/hooks/useUserTheme";

export class BpmnDiffer extends React.Component<BpmnDifferProps, {}> {
    private viewers: { [key: string]: BpmnNavigatedViewer };
    private child: React.RefObject<DiffElementModal>;
    private defaultFillColor = fetchTheme() === ThemeType.light ? S.Colors.white : S.Colors.dark5;

    constructor(props: BpmnDifferProps) {
        super(props);
        this.child = React.createRef<DiffElementModal>();

        this.viewers = {
            [DiffSide.Left]: this.createViewer(this.props.elementParametersLeft),
            [DiffSide.Right]: this.createViewer(this.props.elementParametersRight)
        };
        // sync navigation
        this.syncViewers(this.viewers[DiffSide.Left], this.viewers[DiffSide.Right]);
    }

    public shouldComponentUpdate(nextProps: Readonly<BpmnDifferProps>, nextState: Readonly<{}>, nextContext: any): boolean {
        if (!_.isEqual(this.props, nextProps)) {
            return true;
        }
        return false;
    }

    public componentDidUpdate(prevProps: Readonly<BpmnDifferProps>, prevState: Readonly<{}>, snapshot?: any): void {
        this.loadDiagram(DiffSide.Left, this.props.processLeft?.bpmn);
        this.loadDiagram(DiffSide.Right, this.props.processRight?.bpmn);
    }

    public componentDidMount() {
        this.viewers[DiffSide.Left].attachTo("#canvas-left");
        this.viewers[DiffSide.Right].attachTo("#canvas-right");
        this.loadDiagram(DiffSide.Left, this.props.processLeft?.bpmn);
        this.loadDiagram(DiffSide.Right, this.props.processRight?.bpmn);
    }

    public render() {
        return (<>
            <DiffElementModal {...this.props} ref={this.child} />
            <S.DiffContainer>
                <div className={"di-container " + DiffSide.Left}>
                    <div style={{ height: "100%" }} id={"canvas-" + DiffSide.Left}></div>
                </div >
                <div className={"di-container " + DiffSide.Right}>
                    <div style={{ height: "100%" }} id={"canvas-" + DiffSide.Right}></div>
                </div>
            </S.DiffContainer>
        </>
        );
    }

    private loadDiagram = async (side: DiffSide, diagram: string | undefined) => {
        const viewer = this.viewers[side];
        viewer.loading = true;
        try {
            await viewer.importXML(diagram);
            this.diagramLoaded(side, viewer);

        } catch (err) {
            console.error("load error", err);
        }
    }

    private diagramLoaded = (side: DiffSide, viewer: BpmnNavigatedViewer) => {

        const eventBus = viewer.get("eventBus");
        eventBus.on("element.click", (event) => {
            this.child.current?.openModal(event.element.id);
        });

        // clear possible custom colors
        const modeling = viewer.get("modeling");
        viewer.get("elementRegistry").forEach((element) => {
            modeling.setColor(element, undefined);
        });

        viewer.loading = false;
        if (this.viewers[DiffSide.Left].loading === false && this.viewers[DiffSide.Right].loading === false) {
            // sync viewboxes
            const otherViewer = this.viewers[(side === DiffSide.Left ? DiffSide.Right : DiffSide.Left)];
            viewer.get("canvas").viewbox(otherViewer.get("canvas").viewbox());

            this.showDiff(this.viewers[DiffSide.Left], this.viewers[DiffSide.Right]);
        }
    }

    private showDiff = (viewerOld: BpmnNavigatedViewer, viewerNew: BpmnNavigatedViewer) => {
        const result: { _removed: {}, _added: {}, _layoutChanged: {}, _changed: {} } = diff(viewerOld._definitions, viewerNew._definitions);

        // tslint:disable:forin
        for (const elementId in result._removed) {
            this.highlight(viewerOld, elementId, "diff-removed");
            this.addMarker(viewerOld, elementId, "marker-removed", "&minus;");
        }
        for (const elementId in result._added) {
            this.highlight(viewerNew, elementId, "diff-added");
            this.addMarker(viewerNew, elementId, "marker-added", "&#43;");
        }

        for (const elementId in result._layoutChanged) {
            this.highlight(viewerOld, elementId, "diff-layout-changed");
            this.addMarker(viewerOld, elementId, "marker-layout-changed", "&#8680;");

            this.highlight(viewerNew, elementId, "diff-layout-changed");
            this.addMarker(viewerNew, elementId, "marker-layout-changed", "&#8680;");
        }
        const mergedChanges = { ...result._changed, ...this.props.parameterDiff };
        for (const elementId in mergedChanges) {
            this.highlight(viewerOld, elementId, "diff-changed");
            this.addMarker(viewerOld, elementId, "marker-changed", "&#9998;");

            this.highlight(viewerNew, elementId, "diff-changed");
            this.addMarker(viewerNew, elementId, "marker-changed", "&#9998;");
        }
    }

    private createViewer = (elements: IElementDictionary | null) => {
        const frendsElementStore = new StaticFrendsElementStore(elements);
        return new BpmnNavigatedViewer({
            height: "100%",
            width: "100%",
            additionalModules: [customRenderer, modelingModule, { frendsStore: ["value", frendsElementStore] }],
            canvas: {
                deferUpdate: false
            },
            bpmnRenderer: {
                defaultFillColor: this.defaultFillColor
            }
        });
    }

    private syncViewers(a: BpmnNavigatedViewer, b: BpmnNavigatedViewer) {
        let changing: boolean;
        function update(viewer: BpmnNavigatedViewer) {

            return (event: any) => {
                if (changing) {
                    return;
                }
                changing = true;
                viewer.get("canvas").viewbox(event.viewbox);
                changing = false;
            };
        }

        function syncViewbox(ab: BpmnNavigatedViewer, ba: BpmnNavigatedViewer) {
            ab.on("canvas.viewbox.changed", update(ba));
        }
        syncViewbox(a, b);
        syncViewbox(b, a);
    }

    private highlight = (viewer: BpmnNavigatedViewer, elementId: string, marker: string) => {
        viewer.get("canvas").addMarker(elementId, marker);
    }

    private addMarker = (viewer: BpmnNavigatedViewer, elementId: string, className: string, symbol: string) => {
        const overlays = viewer.get("overlays");
        try {
            // attach an overlay to a node
            overlays.add(elementId, "diff", {
                position: {
                    top: -12,
                    right: 12
                },
                html: '<span class="marker ' + className + '">' + symbol + "</span>"
            });
        } catch (e) {
            // fuck you, haha
        }
    }
}

export interface BpmnDifferProps {
    processLeft: IProcessData | null;
    processRight: IProcessData | null;
    elementParametersLeft: IElementDictionary | null;
    elementParametersRight: IElementDictionary | null;
    parameterDiff: { [key: string]: Array<DiffEdit<{}, {}>> } | null;
}

export enum DiffSide {
    Left = "left",
    Right = "right"
}
